/**
 * The sign-in card, in the new design (PV5-8:B; surfaces.md, sign in): the frosted card standing on the horizon.
 *
 * Everything it does is the shared hook's (src/hooks/useSignInForm.ts) and everything it says is the words module's
 * (src/components/auth/signInWords.ts), the same two today's card reads; this file is only how the card looks. The
 * fields are wet stone (the kit's TextField, the password keeping its exact case), the action is satin gold, Google is
 * Google's own button in a liquid-glass frame, and a failure is the danger notice under the actions. While an attempt
 * runs the card stays and the gold button works (definitions.md: "the gold bar works (the streak); the card stays").
 * The mode switch is the header's one action (./PavilionAuth.tsx), not the card's.
 *
 * The ids and names of today's fields are kept (#email, #current-password, #remember-me, #login-section), so a
 * password manager and anything that finds the form by them find it on either page.
 */
import React from 'react';
import { Button } from '../../kit/button/Button';
import { TextField } from '../../kit/fields/TextField';
import { Checkbox } from '../../kit/selection/Selection';
import { Notice } from '../../kit/feedback/Notice';
import { GoogleSignInButton, GoogleSignInProvider, googleSignInConfigured } from '../../../../components/auth/GoogleSignIn';
import { SIGN_IN_WORDS as W } from '../../../../components/auth/signInWords';
import type { SignInForm } from '../../../../hooks/useSignInForm';
import { SignedInCard } from './SignedInCard';

export interface SignInCardProps {
  form: SignInForm;
  redirectPath: string;
  /** The card's title is the page's h1 when nothing on the sky is (the invitation-code journey), its h2 otherwise. */
  titleLevel: 'h1' | 'h2';
  titleId: string;
}

/**
 * While an attempt runs, Google's button cannot start a second one: its frame is inert (no press, no focus) and says
 * so to a screen reader, as today's card did by turning into the branded transition (go-live audit, 2026-10-02).
 */
const WORKING_FRAME = { inert: '', 'aria-disabled': 'true' } as unknown as React.HTMLAttributes<HTMLDivElement>;

/** Google's button, framed in liquid glass, with what it has to say under it. */
const GoogleArea: React.FC<{ form: SignInForm; working: boolean }> = ({ form, working }) => {
  if (!googleSignInConfigured) {
    return <Notice tone="danger">{W.google.unavailable}</Notice>;
  }
  return (
    <GoogleSignInProvider onScriptLoadError={form.noteGoogleScriptFailed}>
      {form.googleOnlyEmail && (
        <div className="pv-signin-note" data-testid="google-only-prompt">
          <Notice tone="info">
            <strong className="pv-signin-note-title">{W.googleOnly.title}</strong> {W.googleOnly.body(form.googleOnlyEmail)}
          </Notice>
        </div>
      )}
      <div
        ref={form.googleAreaRef}
        className="pv-signin-google"
        data-highlight={form.googleOnlyEmail ? 'true' : undefined}
        data-google-only-highlight={form.googleOnlyEmail ? 'true' : undefined}
        data-working={working ? 'true' : undefined}
        {...(working ? WORKING_FRAME : {})}
      >
        <GoogleSignInButton onSuccess={form.onGoogleSuccess} onError={form.handleGoogleError} />
      </div>
      {form.googleScriptFailed && <Notice tone="danger">{W.google.scriptFailed}</Notice>}
      {form.googleError && <Notice tone="danger">{form.googleError}</Notice>}
      {form.redirectSuffixText && <p className="pv-signin-suffix">{form.redirectSuffixText}</p>}
    </GoogleSignInProvider>
  );
};

export const SignInCard: React.FC<SignInCardProps> = ({ form, redirectPath, titleLevel, titleId }) => {
  const Title = titleLevel;
  const { isSignUp, authStage, inviteFlow, redirectSuffixText } = form;
  const working = authStage === 'submitting';

  if (authStage === 'done') {
    // The beat after it succeeded (today's "account created" / "signed in"), before the page moves on.
    return (
      <div className="pv-signin" id="login-section">
        <Title className="pv-signin-title" id={titleId}>
          ✓ {isSignUp ? W.done.signup : W.done.login}
        </Title>
        <p className="pv-signin-line" role="status">{inviteFlow ? W.done.invite : W.done.other}</p>
      </div>
    );
  }
  if (!working && form.signedInConfirmed) {
    return <SignedInCard redirectPath={redirectPath} titleLevel={titleLevel} titleId={titleId} />;
  }

  return (
    <div className="pv-signin" id="login-section">
      <Title className="pv-signin-title" id={titleId}>
        {isSignUp ? W.mode.signup : W.mode.login}
      </Title>
      <form ref={form.formRef} onSubmit={form.handleLogin} autoComplete="on" className="pv-signin-form">
        <TextField
          label={W.fields.email}
          type="email"
          id="email"
          name="email"
          value={form.email}
          onChange={(e) => form.changeEmail(e.target.value)}
          onKeyDown={form.handleEmailKeyDown}
          // ONE detail token: "username" pairs with the current-password field, so a password manager fills both.
          autoComplete="username"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="next"
          inputRef={form.emailInputRef}
          autoFocus
          required
          readOnly={working}
        />
        <TextField
          label={W.fields.password}
          password
          id="current-password"
          name="password"
          value={form.password}
          onChange={(e) => form.setPassword(e.target.value)}
          onKeyDown={form.handlePasswordKeyDown}
          autoComplete={isSignUp ? 'new-password' : 'current-password'}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          inputRef={form.passwordInputRef}
          required
          readOnly={working}
        />
        <div className="pv-signin-row">
          <Checkbox id="remember-me" label={W.rememberMe} checked={form.rememberMe} onChange={form.setRememberMe} />
          {!isSignUp && (
            <button type="button" className="pv-link pv-signin-forgot" onClick={form.handleForgotPassword} disabled={form.isResettingPassword || working}>
              {form.isResettingPassword ? W.forgot.sending : W.forgot.idle}
            </button>
          )}
        </div>
        <Button variant="primary" type="submit" working={working}>
          {isSignUp ? W.mode.signup : W.mode.login}
          {redirectSuffixText ? <span className="pv-signin-btn-suffix"> {redirectSuffixText}</span> : null}
        </Button>
        {working && (
          // What is happening, in today's words: the card stays, the button works, and this line says what for.
          <p className="pv-signin-line" role="status">
            {isSignUp ? W.working.signup : W.working.login}
            {inviteFlow ? <span className="pv-signin-line-sub"> {W.working.inviteNext}</span> : null}
          </p>
        )}
        {form.resetNotice && <Notice tone="success">{form.resetNotice}</Notice>}
        {form.inlineError && (
          <div className="pv-signin-failure">
            <Notice tone="danger">{form.inlineError}</Notice>
            {form.showCreateInstead && (
              <Button variant="secondary" size="sm" onClick={form.switchToCreateAfterError}>
                {W.createInstead}
              </Button>
            )}
          </div>
        )}
      </form>
      <p className="pv-signin-or">
        <span>{W.or}</span>
      </p>
      <GoogleArea form={form} working={working} />
    </div>
  );
};

/**
 * What the water reflects: a picture of the card's face, never a second form (a second email and password field with
 * the same names, even inert and hidden, is another form to a password manager). The title and the gold bar read as
 * the card's; the fields are their wet-stone boxes, empty.
 */
export const SignInCardPicture: React.FC<{ title: string }> = ({ title }) => (
  <div className="pv-signin pv-signin--picture">
    <p className="pv-signin-title">{title}</p>
    <span className="pv-signin-pic-label" />
    <span className="pv-signin-pic-field pv-well" />
    <span className="pv-signin-pic-label" />
    <span className="pv-signin-pic-field pv-well" />
    <span className="pv-signin-pic-bar pv-gold" />
    <span className="pv-signin-pic-glass" />
  </div>
);
